import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { User, MapPin, Phone, Mail, Ruler, Home, Layers, Compass, ChevronRight, ChevronLeft, AlertCircle, CheckCircle2 } from "lucide-react";

const PROD_BACKEND_URL = "https://vastunet-rdf5v5oyb-anishas-projects-56d465f9.vercel.app";
const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL ||
  (typeof window !== "undefined" && window.location.hostname === "localhost"
    ? "http://localhost:8001"
    : PROD_BACKEND_URL);
const API = `${BACKEND_URL}/api`;

const DIRECTIONS = [
  { value: "North", label: "North", desc: "Uttar - Ruled by Kuber (Wealth)", color: "#3A5A40" },
  { value: "South", label: "South", desc: "Dakshina - Ruled by Yama", color: "#D27C2C" },
  { value: "East", label: "East", desc: "Poorva - Ruled by Indra (Sunrise)", color: "#E9C46A" },
  { value: "West", label: "West", desc: "Paschim - Ruled by Varuna (Strength)", color: "#6B9AC4" },
  { value: "North-East", label: "North-East", desc: "Ishaan - Most auspicious direction", color: "#2D7D46" },
  { value: "North-West", label: "North-West", desc: "Vayavya - Ruled by Vayu (Air)", color: "#7B9E87" },
  { value: "South-East", label: "South-East", desc: "Agneya - Ruled by Agni (Fire)", color: "#C26B2B" },
  { value: "South-West", label: "South-West", desc: "Nairutya - Ruled by Nirrti (Stability)", color: "#8B6914" },
];

const steps = [
  { id: 1, title: "Personal Details", icon: User },
  { id: 2, title: "Plot Details", icon: Ruler },
  { id: 3, title: "Review & Submit", icon: CheckCircle2 },
];

const initialForm = {
  name: "",
  email: "",
  phone: "",
  location: "",
  plot_length: "",
  plot_width: "",
  num_rooms: "",
  num_floors: "",
  facing_direction: "",
  additional_notes: "",
};

export default function FloorPlanForm() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateStep1 = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = "Name is required";
    if (!form.email.trim()) errs.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = "Invalid email address";
    if (!form.phone.trim()) errs.phone = "Phone number is required";
    else if (!/^[6-9]\d{9}$/.test(form.phone.replace(/\s/g, ""))) errs.phone = "Enter a valid 10-digit Indian mobile number";
    if (!form.location.trim()) errs.location = "Location is required";
    return errs;
  };

  const validateStep2 = () => {
    const errs = {};
    if (!form.plot_length || isNaN(form.plot_length) || Number(form.plot_length) <= 0) errs.plot_length = "Valid plot length required";
    if (!form.plot_width || isNaN(form.plot_width) || Number(form.plot_width) <= 0) errs.plot_width = "Valid plot width required";
    if (!form.num_rooms || isNaN(form.num_rooms) || Number(form.num_rooms) < 1) errs.num_rooms = "Number of rooms required";
    if (!form.num_floors || isNaN(form.num_floors) || Number(form.num_floors) < 1) errs.num_floors = "Number of floors required";
    if (!form.facing_direction) errs.facing_direction = "Please select the facing direction";
    return errs;
  };

  const handleNext = () => {
    let errs = {};
    if (step === 1) errs = validateStep1();
    if (step === 2) errs = validateStep2();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStep((s) => s + 1);
  };

  const handleBack = () => {
    setStep((s) => s - 1);
    setErrors({});
  };

  const handleSubmit = async () => {
    setLoading(true);
    setSubmitError("");
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        location: form.location.trim(),
        plot_length: parseFloat(form.plot_length),
        plot_width: parseFloat(form.plot_width),
        num_rooms: parseInt(form.num_rooms),
        num_floors: parseInt(form.num_floors),
        facing_direction: form.facing_direction,
        additional_notes: form.additional_notes.trim() || null,
      };
      await axios.post(`${API}/enquiries`, payload);
      sessionStorage.setItem("latestEnquiry", JSON.stringify(payload));
      navigate("/thank-you?submitted=1", { state: { data: payload } });
    } catch (err) {
      console.error(err);
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const selectedDirection = DIRECTIONS.find((d) => d.value === form.facing_direction);

  return (
    <div className="min-h-screen bg-vastu-cream paper-texture pt-20" data-testid="floor-plan-form-page">
      {/* Header */}
      <div
        className="py-16 text-center relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #2D2A26 0%, #3A5A40 100%)" }}
      >
        <div className="absolute inset-0 blueprint-grid opacity-20" />
        <div className="relative max-w-3xl mx-auto px-6">
          <p className="section-eyebrow mb-3" style={{ color: "#E9C46A" }}>
            Step Towards Your Dream Home
          </p>
          <h1 className="font-heading text-4xl md:text-5xl font-light text-white mb-4">
            Get Your Vastu-Compliant Floor Plan
          </h1>
          <p className="font-body text-base md:text-lg" style={{ color: "rgba(255,255,255,0.7)" }}>
            Fill in your details below and our Vastu experts will design the perfect floor plan for your home.
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center gap-4 mb-10" data-testid="form-steps">
          {steps.map((s, i) => {
            const Icon = s.icon;
            const isActive = step === s.id;
            const isDone = step > s.id;
            return (
              <div key={s.id} className="flex items-center">
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center font-body font-semibold text-sm transition-all duration-300 ${
                      isDone
                        ? "bg-vastu-green text-white shadow-lg"
                        : isActive
                        ? "text-white shadow-lg"
                        : "bg-vastu-muted text-vastu-mutedFg"
                    }`}
                    style={isActive ? { background: "#D27C2C" } : {}}
                  >
                    {isDone ? <CheckCircle2 size={20} /> : <Icon size={20} />}
                  </div>
                  <span
                    className={`font-body text-xs font-medium hidden sm:block ${
                      isActive ? "text-vastu-terracotta" : isDone ? "text-vastu-green" : "text-vastu-mutedFg"
                    }`}
                  >
                    {s.title}
                  </span>
                </div>
                {i < steps.length - 1 && (
                  <div
                    className="w-16 sm:w-24 h-0.5 mx-2 transition-all duration-300"
                    style={{ background: step > s.id ? "#3A5A40" : "#DCD7C9" }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Form Card */}
        <div className="vastu-card shadow-xl">
          {/* Step 1: Personal Details */}
          {step === 1 && (
            <div data-testid="step-1-personal">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(210, 124, 44, 0.1)" }}>
                  <User size={20} style={{ color: "#D27C2C" }} />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-semibold" style={{ color: "#2D2A26" }}>
                    Personal Details
                  </h2>
                  <p className="font-body text-sm text-vastu-mutedFg">Tell us about yourself</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="font-body text-sm font-medium text-vastu-charcoal mb-2 flex items-center gap-1">
                    <User size={14} style={{ color: "#D27C2C" }} />
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="e.g. Rajesh Kumar"
                    className="form-input-vastu"
                    data-testid="input-name"
                  />
                  {errors.name && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="font-body text-sm font-medium text-vastu-charcoal mb-2 flex items-center gap-1">
                    <Mail size={14} style={{ color: "#D27C2C" }} />
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="e.g. rajesh@example.com"
                    className="form-input-vastu"
                    data-testid="input-email"
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="font-body text-sm font-medium text-vastu-charcoal mb-2 flex items-center gap-1">
                    <Phone size={14} style={{ color: "#D27C2C" }} />
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={form.phone}
                    onChange={handleChange}
                    placeholder="e.g. 9876543210"
                    className="form-input-vastu"
                    maxLength={10}
                    data-testid="input-phone"
                  />
                  {errors.phone && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.phone}
                    </p>
                  )}
                </div>

                <div>
                  <label className="font-body text-sm font-medium text-vastu-charcoal mb-2 flex items-center gap-1">
                    <MapPin size={14} style={{ color: "#D27C2C" }} />
                    City / Location *
                  </label>
                  <input
                    type="text"
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    placeholder="e.g. Mumbai, Maharashtra"
                    className="form-input-vastu"
                    data-testid="input-location"
                  />
                  {errors.location && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.location}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Plot Details */}
          {step === 2 && (
            <div data-testid="step-2-plot">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(58, 90, 64, 0.1)" }}>
                  <Ruler size={20} style={{ color: "#3A5A40" }} />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-semibold" style={{ color: "#2D2A26" }}>
                    Plot Details
                  </h2>
                  <p className="font-body text-sm text-vastu-mutedFg">Share your plot specifications</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="font-body text-sm font-medium text-vastu-charcoal mb-2 flex items-center gap-1">
                    <Ruler size={14} style={{ color: "#3A5A40" }} />
                    Plot Length (in feet) *
                  </label>
                  <input
                    type="number"
                    name="plot_length"
                    value={form.plot_length}
                    onChange={handleChange}
                    placeholder="e.g. 30"
                    className="form-input-vastu"
                    min="1"
                    data-testid="input-plot-length"
                  />
                  {errors.plot_length && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.plot_length}
                    </p>
                  )}
                </div>

                <div>
                  <label className="font-body text-sm font-medium text-vastu-charcoal mb-2 flex items-center gap-1">
                    <Ruler size={14} style={{ color: "#3A5A40" }} />
                    Plot Width (in feet) *
                  </label>
                  <input
                    type="number"
                    name="plot_width"
                    value={form.plot_width}
                    onChange={handleChange}
                    placeholder="e.g. 40"
                    className="form-input-vastu"
                    min="1"
                    data-testid="input-plot-width"
                  />
                  {errors.plot_width && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.plot_width}
                    </p>
                  )}
                </div>

                <div>
                  <label className="font-body text-sm font-medium text-vastu-charcoal mb-2 flex items-center gap-1">
                    <Home size={14} style={{ color: "#3A5A40" }} />
                    Number of Rooms *
                  </label>
                  <select
                    name="num_rooms"
                    value={form.num_rooms}
                    onChange={handleChange}
                    className="form-input-vastu"
                    data-testid="input-num-rooms"
                  >
                    <option value="">Select number of rooms</option>
                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                      <option key={n} value={n}>{n} Room{n > 1 ? "s" : ""} ({n + 1} BHK equivalent)</option>
                    ))}
                  </select>
                  {errors.num_rooms && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.num_rooms}
                    </p>
                  )}
                </div>

                <div>
                  <label className="font-body text-sm font-medium text-vastu-charcoal mb-2 flex items-center gap-1">
                    <Layers size={14} style={{ color: "#3A5A40" }} />
                    Number of Floors *
                  </label>
                  <select
                    name="num_floors"
                    value={form.num_floors}
                    onChange={handleChange}
                    className="form-input-vastu"
                    data-testid="input-num-floors"
                  >
                    <option value="">Select number of floors</option>
                    {[1, 2, 3, 4, 5].map((n) => (
                      <option key={n} value={n}>{n} Floor{n > 1 ? "s" : ""}{n === 1 ? " (Ground)" : ""}</option>
                    ))}
                  </select>
                  {errors.num_floors && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <AlertCircle size={12} /> {errors.num_floors}
                    </p>
                  )}
                </div>
              </div>

              {/* Plot Area Display */}
              {form.plot_length && form.plot_width && (
                <div
                  className="rounded-xl p-4 mb-6 flex items-center gap-3"
                  style={{ background: "rgba(210, 124, 44, 0.08)", border: "1px solid rgba(210, 124, 44, 0.2)" }}
                >
                  <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "#D27C2C" }}>
                    <Ruler size={14} className="text-white" />
                  </div>
                  <div>
                    <p className="font-body text-xs text-vastu-mutedFg">Calculated Plot Area</p>
                    <p className="font-body font-semibold" style={{ color: "#D27C2C" }}>
                      {(parseFloat(form.plot_length) * parseFloat(form.plot_width)).toFixed(0)} sq ft
                      ({form.plot_length} × {form.plot_width} feet)
                    </p>
                  </div>
                </div>
              )}

              {/* Direction Selector */}
              <div>
                <label className="font-body text-sm font-medium text-vastu-charcoal mb-3 flex items-center gap-1">
                  <Compass size={14} style={{ color: "#3A5A40" }} />
                  Plot Facing Direction *
                </label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="direction-selector">
                  {DIRECTIONS.map((dir) => (
                    <button
                      type="button"
                      key={dir.value}
                      onClick={() => {
                        setForm((prev) => ({ ...prev, facing_direction: dir.value }));
                        if (errors.facing_direction) setErrors((prev) => ({ ...prev, facing_direction: "" }));
                      }}
                      className={`p-3 rounded-xl border-2 text-left transition-all duration-300 ${
                        form.facing_direction === dir.value
                          ? "shadow-lg scale-[1.02]"
                          : "border-vastu-border hover:border-vastu-terracotta/50 bg-white/50"
                      }`}
                      style={
                        form.facing_direction === dir.value
                          ? { borderColor: dir.color, background: `${dir.color}15` }
                          : {}
                      }
                      data-testid={`direction-${dir.value.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    >
                      <p
                        className="font-body font-semibold text-sm mb-1"
                        style={{ color: form.facing_direction === dir.value ? dir.color : "#2D2A26" }}
                      >
                        {dir.label}
                      </p>
                      <p className="font-body text-xs text-vastu-mutedFg leading-tight">{dir.desc}</p>
                    </button>
                  ))}
                </div>
                {errors.facing_direction && (
                  <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
                    <AlertCircle size={12} /> {errors.facing_direction}
                  </p>
                )}
              </div>

              {/* Additional Notes */}
              <div className="mt-6">
                <label className="font-body text-sm font-medium text-vastu-charcoal mb-2 block">
                  Additional Requirements (Optional)
                </label>
                <textarea
                  name="additional_notes"
                  value={form.additional_notes}
                  onChange={handleChange}
                  placeholder="Any special requirements, existing structure details, or preferences..."
                  className="form-input-vastu resize-none"
                  rows={3}
                  data-testid="input-additional-notes"
                />
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div data-testid="step-3-review">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "rgba(233, 196, 106, 0.2)" }}>
                  <CheckCircle2 size={20} style={{ color: "#D27C2C" }} />
                </div>
                <div>
                  <h2 className="font-heading text-2xl font-semibold" style={{ color: "#2D2A26" }}>
                    Review Your Details
                  </h2>
                  <p className="font-body text-sm text-vastu-mutedFg">Please verify all information before submitting</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {/* Personal */}
                <div
                  className="rounded-2xl p-5"
                  style={{ background: "rgba(210, 124, 44, 0.05)", border: "1px solid rgba(210, 124, 44, 0.2)" }}
                >
                  <h3 className="font-heading text-lg font-semibold mb-4" style={{ color: "#D27C2C" }}>
                    Personal Details
                  </h3>
                  <div className="space-y-2 font-body text-sm">
                    <div className="flex justify-between">
                      <span className="text-vastu-mutedFg">Name</span>
                      <span className="font-medium text-vastu-charcoal">{form.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-vastu-mutedFg">Email</span>
                      <span className="font-medium text-vastu-charcoal truncate ml-2">{form.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-vastu-mutedFg">Phone</span>
                      <span className="font-medium text-vastu-charcoal">{form.phone}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-vastu-mutedFg">Location</span>
                      <span className="font-medium text-vastu-charcoal">{form.location}</span>
                    </div>
                  </div>
                </div>

                {/* Plot */}
                <div
                  className="rounded-2xl p-5"
                  style={{ background: "rgba(58, 90, 64, 0.05)", border: "1px solid rgba(58, 90, 64, 0.2)" }}
                >
                  <h3 className="font-heading text-lg font-semibold mb-4" style={{ color: "#3A5A40" }}>
                    Plot Details
                  </h3>
                  <div className="space-y-2 font-body text-sm">
                    <div className="flex justify-between">
                      <span className="text-vastu-mutedFg">Dimensions</span>
                      <span className="font-medium text-vastu-charcoal">{form.plot_length} × {form.plot_width} ft</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-vastu-mutedFg">Total Area</span>
                      <span className="font-medium text-vastu-charcoal">{(form.plot_length * form.plot_width).toFixed(0)} sq ft</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-vastu-mutedFg">Rooms</span>
                      <span className="font-medium text-vastu-charcoal">{form.num_rooms}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-vastu-mutedFg">Floors</span>
                      <span className="font-medium text-vastu-charcoal">{form.num_floors}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-vastu-mutedFg">Facing</span>
                      <span className="font-medium" style={{ color: selectedDirection?.color }}>
                        {form.facing_direction}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {form.additional_notes && (
                <div
                  className="rounded-2xl p-4 mb-6 font-body text-sm"
                  style={{ background: "rgba(233, 196, 106, 0.1)", border: "1px solid rgba(233, 196, 106, 0.3)" }}
                >
                  <p className="font-medium text-vastu-charcoal mb-1">Additional Notes:</p>
                  <p className="text-vastu-mutedFg">{form.additional_notes}</p>
                </div>
              )}

              {/* Vastu note */}
              <div
                className="rounded-xl p-4 mb-6 flex items-start gap-3"
                style={{ background: "rgba(210, 124, 44, 0.06)", border: "1px solid rgba(210, 124, 44, 0.15)" }}
              >
                <Compass size={18} style={{ color: "#D27C2C", flexShrink: 0, marginTop: 2 }} />
                <p className="font-body text-xs text-vastu-mutedFg">
                  <strong style={{ color: "#2D2A26" }}>Vastu Expert Assigned:</strong> Once submitted, a certified Vastu 
                  consultant will review your details and prepare a customized floor plan within 24-48 hours.
                </p>
              </div>

              {submitError && (
                <div
                  className="rounded-xl p-4 mb-4 flex items-center gap-2"
                  style={{ background: "rgba(220, 38, 38, 0.08)", border: "1px solid rgba(220, 38, 38, 0.2)" }}
                  data-testid="submit-error"
                >
                  <AlertCircle size={16} className="text-red-500 flex-shrink-0" />
                  <p className="font-body text-sm text-red-600">{submitError}</p>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-vastu-border">
            {step > 1 ? (
              <button
                type="button"
                onClick={handleBack}
                className="btn-secondary text-sm py-3 px-6"
                data-testid="back-btn"
              >
                <ChevronLeft size={18} />
                Back
              </button>
            ) : (
              <div />
            )}

            {step < 3 ? (
              <button
                type="button"
                onClick={handleNext}
                className="btn-primary text-sm py-3 px-6"
                data-testid="next-btn"
              >
                Next Step
                <ChevronRight size={18} />
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={loading}
                className="btn-primary text-sm py-3 px-8"
                data-testid="submit-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Submitting...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Submit Enquiry
                    <CheckCircle2 size={18} />
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
